import { ICertReport } from '@food-certification-app/app/components/side-panel/due-dates-panel/shared/interfaces/cert-report.interface';
import { DueDatesPanelService } from '@food-certification-app/app/components/side-panel/due-dates-panel/shared/services/due-dates-panel.service';
import { ECertificationType } from '@food-certification-app/app/pages/audit-details/components/audit-details-scope/shared/components/edit-scope/shared/components/edit-scope-general-information/shared/models/interfaces/certification-type-and-add-on.interface';
import { IOrderAddOn } from '@food-certification-app/app/pages/product-sites-facilities/shared/models/interfaces/product-sites-facilities.interface';
import { inject } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

/**
 * Maps cert-reports to order add-ons (`type === ADDON`).
 * @param {ICertReport[]} reports - Cert reports from the order API.
 * @returns {IOrderAddOn[]} Add-ons sorted alphabetically by label.
 */
export function mapCertReportsToOrderAddOns(reports: ICertReport[]): IOrderAddOn[] {
  return (reports ?? [])
    .filter((report: ICertReport): boolean => report.type === ECertificationType.ADDON)
    .map(
      (report: ICertReport): IOrderAddOn => ({
        id: report.id,
        key: report.certTypeKey,
        label: report.certTypeText || report.certTypeKey,
      })
    )
    .sort((a: IOrderAddOn, b: IOrderAddOn): number => a.label.localeCompare(b.label));
}

/**
 * Prefetches order add-ons before entering product-sites-facilities.
 * Result is available as `route.snapshot.data['orderAddOns']`.
 * @param {ActivatedRouteSnapshot} route - Activated route snapshot.
 * @returns {Observable<IOrderAddOn[]>} Resolved add-ons.
 */
export function orderAddOnsResolver$(route: ActivatedRouteSnapshot): Observable<IOrderAddOn[]> {
  const orderId = route.paramMap.get('orderNumber') ?? route.parent?.paramMap.get('orderNumber') ?? '';

  if (!orderId) {
    return of([]);
  }

  return inject(DueDatesPanelService)
    .getCertReports$(orderId)
    .pipe(map((reports: ICertReport[]): IOrderAddOn[] => mapCertReportsToOrderAddOns(reports)));
}
